import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useParams } from "react-router";
import Breadcrumb from "../../../shared/common/breadcrumb";
import { UserContentBanner } from "../../../shared/common/userContentBanner";
import { IBreadcrumb } from "../../../../model/interface/breadcrumb";
import Constant from "../../../../utils/constant";
import { getUserInfo, IUserInfo } from "../../../../api/crmContent.service";
import ForbiddenAccess from "../../../shared/common/forbiddenAccess";
import { userPermissions } from "../../../shared/common/rbac";
import Permissions from "../../../../utils/permissions";

export interface IUserContentOutletContext {
  userId: number;
  userInfo?: IUserInfo;
}

const CHILD_CRUMB_LABELS: Record<string, string> = {
  passages: "Passage Bank",
  "passages/new": "Create Passage",
  questions: "Question Bank",
  "questions/new": "Create Question",
  assessments: "Assessment Bank",
  "assessments/new": "Create Assessment",
};

export const UserContentLayout: React.FC = () => {
  const { userid } = useParams<{ userid: string }>();
  const userId = Number(userid);
  const { pathname } = useLocation();
  const [userInfo, setUserInfo] = useState<IUserInfo | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [lookupFailed, setLookupFailed] = useState<boolean>(false);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setLoading(true);
    setLookupFailed(false);
    getUserInfo(userId)
      .then((res) => {
        if (!cancelled) setUserInfo(res.data);
      })
      .catch(() => {
        if (!cancelled) setLookupFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (!userPermissions().includes(Permissions.crm_content_view)) {
    return <ForbiddenAccess />;
  }

  if (
    !loading &&
    (lookupFailed ||
      (userInfo && userInfo.roleId !== Constant.UserRoleId.District))
  ) {
    return (
      <ForbiddenAccess message="This user is not a District Admin and cannot have content authored on their behalf." />
    );
  }

  const contentBaseUrl = `/crm/users/${userId}/content`;
  const childPath = pathname.startsWith(`${contentBaseUrl}/`)
    ? pathname.slice(contentBaseUrl.length + 1).replace(/\/$/, "")
    : "";
  const editPassageMatch = /^passages\/\d+$/.test(childPath);
  const editQuestionMatch = /^questions\/\d+$/.test(childPath);
  const editAssessmentMatch = /^assessments\/\d+$/.test(childPath);
  const childLabel = editPassageMatch
    ? "Edit Passage"
    : editQuestionMatch
    ? "Edit Question"
    : editAssessmentMatch
    ? "Edit Assessment"
    : CHILD_CRUMB_LABELS[childPath];
  const pageTitle = childLabel ?? "Content";

  const breadcrumbItems = (): Array<IBreadcrumb> => {
    const items: Array<IBreadcrumb> = [
      { name: "Mini CRM", url: Constant.CRM },
      { name: "Content", url: childLabel ? contentBaseUrl : "" },
    ];
    if (editPassageMatch) {
      items.push({ name: "Passage Bank", url: `${contentBaseUrl}/passages` });
      items.push({ name: "Edit Passage", url: "" });
    } else if (editQuestionMatch) {
      items.push({ name: "Question Bank", url: `${contentBaseUrl}/questions` });
      items.push({ name: "Edit Question", url: "" });
    } else if (editAssessmentMatch) {
      items.push({
        name: "Assessment Bank",
        url: `${contentBaseUrl}/assessments`,
      });
      items.push({ name: "Edit Assessment", url: "" });
    } else if (childLabel) {
      items.push({ name: childLabel, url: "" });
    }
    return items;
  };

  return (
    <div className="flex-1 overflow-auto">
      <UserContentBanner
        firstName={userInfo?.firstName}
        lastName={userInfo?.lastName}
        district={userInfo?.district}
        loading={loading}
      />
      <div className="h-auto w-full rounded-bl-3xl bg-site-teal px-8 pt-2 pb-2">
        <div className="relative py-1">
          <div className="overlay-image-main pointer-events-none fixed left-0 top-0 w-screen h-screen z-20"></div>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-light text-gray-900">{pageTitle}</h3>
          </div>
          <Breadcrumb items={breadcrumbItems()} />
        </div>
      </div>
      <Outlet
        context={{ userId, userInfo } satisfies IUserContentOutletContext}
      />
    </div>
  );
};
